/** The feature area a permission belongs to, used to group the permission UI. */
export enum PermissionGroup {
  platform = "platform",
  staff = "staff",
  profiles = "profiles",
  groups = "groups",
  accounting = "accounting",
  submissions = "submissions",
  reviews = "reviews",
  conferences = "conferences",
  reports = "reports",
  forms = "forms",
  notifications = "notifications",
  dashboards = "dashboards",
  chatbot = "chatbot",
  settings = "settings",
  workflows = "workflows",
}

export type PermissionGroupDefinition = {
  displayName: string;
  description: string;
};

/** Display metadata for each {@link PermissionGroup}. */
export const permissionGroupDefinitions: Record<
  PermissionGroup,
  PermissionGroupDefinition
> = {
  [PermissionGroup.platform]: {
    displayName: "Platform Administration",
    description:
      "Cross-society staff tooling. These are global permissions and are not scoped to a single society.",
  },
  [PermissionGroup.staff]: {
    displayName: "Staff Notes & Timeline",
    description:
      "Internal notes and timeline entries staff keep about a society.",
  },
  [PermissionGroup.profiles]: {
    displayName: "Profiles",
    description:
      "Member profile search and the individual tabs of a profile record.",
  },
  [PermissionGroup.groups]: {
    displayName: "Groups",
    description: "Groups, committees and their membership terms.",
  },
  [PermissionGroup.accounting]: {
    displayName: "Accounting",
    description: "Invoices, payments, refunds, discounts and deferments.",
  },
  [PermissionGroup.submissions]: {
    displayName: "Submissions",
    description:
      "Submission definitions and the submissions members have made against them.",
  },
  [PermissionGroup.reviews]: {
    displayName: "Reviews",
    description: "Review sessions, reviewer assignments and recusals.",
  },
  [PermissionGroup.conferences]: {
    displayName: "Conferences",
    description: "Conferences, their schedules and their events.",
  },
  [PermissionGroup.reports]: {
    displayName: "Reports",
    description: "Saved reports and the report builder.",
  },
  [PermissionGroup.forms]: {
    displayName: "Forms",
    description: "Form definitions and the profile entry point they gate.",
  },
  [PermissionGroup.notifications]: {
    displayName: "Notifications",
    description: "Notifications sent to the society members.",
  },
  [PermissionGroup.dashboards]: {
    displayName: "Dashboards",
    description: "The society dashboard pages.",
  },
  [PermissionGroup.chatbot]: {
    displayName: "Chatbot",
    description: "The society chatbot and its configuration.",
  },
  [PermissionGroup.settings]: {
    displayName: "Settings",
    description: "Every tab of the society settings area.",
  },
  [PermissionGroup.workflows]: {
    displayName: "Workflows",
    description: "The workflow builder and running workflow instances.",
  },
};

/** The order groups are rendered in the permission management UI. */
export const permissionGroupOrder: PermissionGroup[] = [
  PermissionGroup.platform,
  PermissionGroup.staff,
  PermissionGroup.profiles,
  PermissionGroup.groups,
  PermissionGroup.accounting,
  PermissionGroup.submissions,
  PermissionGroup.reviews,
  PermissionGroup.conferences,
  PermissionGroup.reports,
  PermissionGroup.forms,
  PermissionGroup.notifications,
  PermissionGroup.dashboards,
  PermissionGroup.chatbot,
  PermissionGroup.settings,
  PermissionGroup.workflows,
];
